import { getReferenceString, ProfileResource } from '@medplum/core';
import { Practitioner, Reference } from '@medplum/fhirtypes';
import {
  isFaxTimelineCommunication,
  TimelineNote,
  TimelinePatientCommunication,
  TimelineTaskCompleted,
} from '@/components/Timeline/types';
import { normalizeTextContent } from '@bariendo/lib/services';

/**
 * Generic function to filter communications and notes with related item inclusion.
 * When a note matches, its associated communication is also included.
 * @param timelineRegisters - Array of timeline registers to filter
 * @param timelineNotes - Array of timeline notes to filter
 * @param predicate - Function to test if an item matches (works on both TimelineRegister and TimelineNote)
 * @returns Tuple of filtered communications and notes
 */
function filterWithRelatedItems(
  timelineRegisters: TimelinePatientCommunication[],
  timelineNotes: TimelineNote[],
  predicate: (item: TimelinePatientCommunication | TimelineNote) => boolean
): [TimelinePatientCommunication[], TimelineNote[]] {
  const matchingRefs = new Set<string>();

  // Find matching communications
  timelineRegisters.forEach((item) => {
    if (predicate(item)) {
      const refString = getReferenceString(item.reference);
      if (refString) matchingRefs.add(refString);
    }
  });

  // Find matching notes and include their associated communications
  timelineNotes.forEach((note) => {
    if (predicate(note)) {
      const refString = getReferenceString(note.reference);
      if (refString) matchingRefs.add(refString);
      // Add the related communication
      if (note.linkedTo) {
        matchingRefs.add(getReferenceString(note.linkedTo)!);
      }
    }
  });

  // Filter based on matching references
  const filteredItems = timelineRegisters.filter((item) => {
    const refString = getReferenceString(item.reference);
    return refString && matchingRefs.has(refString);
  });
  const filteredNotes = timelineNotes.filter((note) => {
    const refString = getReferenceString(note.reference);
    return refString && matchingRefs.has(refString);
  });

  return [filteredItems, filteredNotes];
}

export function filterCommunications(
  timelineRegisters: TimelinePatientCommunication[],
  timelineNotes: TimelineNote[],
  searchTerm: string,
  provider?: Reference<Practitioner> | null,
  startDate?: Date | null,
  endDate?: Date | null
): [TimelinePatientCommunication[], TimelineNote[]] {
  let filteredItems = timelineRegisters;
  let filteredNotes = timelineNotes;

  // Filter by date range if provided
  if (startDate || endDate) {
    [filteredItems, filteredNotes] = filterWithRelatedItems(filteredItems, filteredNotes, (item) => {
      const itemDate = item.timestamp;

      // Compare start date at beginning of day (00:00:00)
      if (startDate) {
        const startOfStartDate = new Date(startDate);
        startOfStartDate.setHours(0, 0, 0, 0);
        if (itemDate < startOfStartDate) return false;
      }

      // Compare end date at end of day (23:59:59.999) to be inclusive
      if (endDate) {
        const endOfEndDate = new Date(endDate);
        endOfEndDate.setHours(23, 59, 59, 999);
        if (itemDate > endOfEndDate) return false;
      }

      return true;
    });
  }

  // Filter by provider if selected
  if (provider) {
    const selectedProviderRef = getReferenceString(provider);
    [filteredItems, filteredNotes] = filterWithRelatedItems(filteredItems, filteredNotes, (item) =>
      item.sender ? getReferenceString(item.sender as ProfileResource) === selectedProviderRef : false
    );
  }

  // Filter by search term if provided
  if (searchTerm.trim()) {
    const searchLower = normalizeTextContent(searchTerm);

    [filteredItems, filteredNotes] = filterWithRelatedItems(filteredItems, filteredNotes, (item) => {
      const haystack = normalizeTextContent(getTimelineItemSearchHaystack(item));
      return haystack.includes(searchLower);
    });
  }

  return [filteredItems, filteredNotes];
}

/**
 * Applies the timeline's search / date-range / provider filters to the
 * "Task completed" rows, using the same matching rules as communications:
 * - search matches the task title (what the row renders);
 * - date range compares the completion timestamp;
 * - provider matches WHO COMPLETED it (the row's actor), which is the
 *   task-row analogue of a communication's sender.
 * Without this the rows ignored every filter and sat in the results of
 * searches they did not match.
 */
export function filterCompletedTaskItems(
  items: TimelineTaskCompleted[],
  searchTerm: string,
  provider?: Reference<Practitioner> | null,
  startDate?: Date | null,
  endDate?: Date | null
): TimelineTaskCompleted[] {
  let filtered = items;

  if (startDate || endDate) {
    filtered = filtered.filter((item) => {
      if (startDate) {
        const startOfStartDate = new Date(startDate);
        startOfStartDate.setHours(0, 0, 0, 0);
        if (item.timestamp < startOfStartDate) return false;
      }
      if (endDate) {
        const endOfEndDate = new Date(endDate);
        endOfEndDate.setHours(23, 59, 59, 999);
        if (item.timestamp > endOfEndDate) return false;
      }
      return true;
    });
  }

  if (provider) {
    const selectedProviderRef = getReferenceString(provider);
    filtered = filtered.filter((item) =>
      item.completedBy ? getReferenceString(item.completedBy) === selectedProviderRef : false
    );
  }

  if (searchTerm.trim()) {
    const searchLower = normalizeTextContent(searchTerm);
    filtered = filtered.filter((item) => normalizeTextContent(item.title).includes(searchLower));
  }

  return filtered;
}

function getTimelineItemSearchHaystack(item: TimelinePatientCommunication | TimelineNote): string {
  const parts: string[] = [];
  if ('content' in item && item.content) {
    parts.push(item.content);
  }
  if (isFaxTimelineCommunication(item)) {
    parts.push(item.faxCounterparty);
    for (const att of [...(item.pdfAttachments ?? []), ...(item.mediaAttachments ?? [])]) {
      if (att.title) {
        parts.push(att.title);
      }
      if (att.url) {
        parts.push(att.url);
      }
    }
  }
  return parts.join(' ');
}
